import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { composeMiddleware } from "@/store2/middleware";
import { StoreGet, StoreSet } from "@/types";
import {
  IPictureTopUpAction,
  IPictureTopUpState,
  IPictureTopUpStore,
  IVerifyRechargeCodeParams,
} from "../types";
import { commonActions } from "@/store2/common";
import { ActivityTypeCode, CouponTypeCode } from "project-config/constants";
import { payWithAli } from "@/components/top-up/help/payWithAli";
import { handlePaycode } from "@/utils";
import { PictureTopUpAPI } from "./service";
import { BindActivityInfo } from "@/components/top-up/types";

const defaultDesktopActivity = () => ({
  id: -1,
  activityName: "云制作币充值",
  activityNameEn: "Recharge of Cloud Production Coins",
  startDate: 1734315979000,
  endDate: 9760083093043,
  status: 1,
  partInTime: null,
  buriedPointId: null,
  pictureUrl: null,
  jumpUrl: null,
  rulesCn:
    "一、充值规则\n1、充值金额与云制作币为1:1兑换\n2、如有其他充值问题，请联系在线客服\n二、充值金额：1～200000\n三、展示当前账号云制作币余额与云制作币累积充值金额",
  rulesEn:
    "1. Recharge Rules\n1. The exchange ratio between the recharge amount and Cloud Production Coins is 1:1.\n2. For other recharge-related issues, please contact online customer service.\n2. Recharge Amount: 1～200000\n3. Display the current account's Cloud Production Coin balance and the cumulative recharge amount of Cloud Production Coins.",
  presentType: 0,
  rechargeType: null,
  remark: "",
  activityAdType: 0,
  imageUrlLink: "",
  imageUrlLinkEventDetails: "",
  chineseSlogan: "",
  englishSlogan: "",
  activityType: 4,
  giftType: -1,
  darkImageUrl: "",
  lightImageUrl: "",
  pictureImageUrlLink: "",
  maxCouponAmount: null,
  activityDetail: [
    {
      id: 1,
      activityTemplateId: 1,
      vipAmount: null,
      vipPresentAmount: null,
      amount: 1000,
      couponType: CouponTypeCode.Desktop,
      presentAmount: 1000,
      level: null,
      lotteryCount: null,
      surveyLotteryCount: null,
      createDate: 1734316418000,
      updateDate: 1734316418000,
      prizeType: null,
      prizeName: null,
      prizeCount: null,
      iconWord: null,
      isSendVoucher: 1,
    },
    {
      id: 2,
      activityTemplateId: 2,
      vipAmount: null,
      vipPresentAmount: null,
      amount: 5000,
      couponType: CouponTypeCode.Desktop,
      presentAmount: 5000,
      level: null,
      lotteryCount: null,
      surveyLotteryCount: null,
      createDate: 1734316418000,
      updateDate: 1734316418000,
      prizeType: null,
      prizeName: null,
      prizeCount: null,
      iconWord: null,
      isSendVoucher: 1,
    },
    {
      id: 3,
      activityTemplateId: 3,
      vipAmount: null,
      vipPresentAmount: null,
      amount: 10000,
      couponType: CouponTypeCode.Desktop,
      presentAmount: 10000,
      level: null,
      lotteryCount: null,
      surveyLotteryCount: null,
      createDate: 1734316418000,
      updateDate: 1734316418000,
      prizeType: null,
      prizeName: null,
      prizeCount: null,
      iconWord: null,
      isSendVoucher: 1,
    },
    {
      id: 4,
      activityTemplateId: 4,
      vipAmount: null,
      vipPresentAmount: null,
      amount: 20000,
      couponType: CouponTypeCode.Desktop,
      presentAmount: 20000,
      level: null,
      lotteryCount: null,
      surveyLotteryCount: null,
      createDate: 1734316418000,
      updateDate: 1734316418000,
      prizeType: null,
      prizeName: null,
      prizeCount: null,
      iconWord: "",
      isSendVoucher: 1,
    },
  ],
});

export const initVerifyRechargeInfo = {
  activityId: 0,
  couponAmount: 0,
  couponNO: "",
  rechargeAmount: 0,
  couponType: CouponTypeCode.Universal,
};

const initialState = (): IPictureTopUpState => {
  return {
    activeName: "",
    rechargeSecondActiveName: "", // 充值tab的二级tab
    vipSecondActiveName: "", // 会员订阅tab的二级tab
    activityInfoList: [],
    amount: 0,
    desktopAmount: null,
    hasScanned: false,
    vipMemberType: null,
    paying: false,
    isCheckCard: true,
    showCouponCodeModal: false,
    inputCode: "",
    verifyRechargeInfo: initVerifyRechargeInfo,
  };
};

const getter = (get: StoreGet<IPictureTopUpStore>) => ({
  // 是否存在赠送会员活动（效果图）
  haveGiftMemberActivity: () => {
    const { activityInfoList } = get();
    return activityInfoList.some(
      (item) =>
        item.activityType === ActivityTypeCode.GiftMember &&
        item.activityDetail[0].vipAmount
    );
  },
  // 赠送会员活动（效果图）
  giftMemberActivity: () => {
    const { activityInfoList, haveGiftMemberActivity } = get();
    if (haveGiftMemberActivity()) {
      return activityInfoList.find(
        (item) =>
          item.activityType === ActivityTypeCode.GiftMember &&
          item.activityDetail[0].vipAmount
      );
    } else {
      return {};
    }
  },
  // 是否存在充值赠送活动
  haveRechargeGiftActivity: () => {
    const { activityInfoList } = get();
    return activityInfoList.some(
      (item) => item.activityType === ActivityTypeCode.RechargeGift
    );
  },
  // 充值赠送活动
  rechargeGiftActivity: () => {
    const { activityInfoList, haveRechargeGiftActivity } = get();
    if (haveRechargeGiftActivity()) {
      return activityInfoList.find(
        (item) => item.activityType === ActivityTypeCode.RechargeGift
      );
    } else {
      return {};
    }
  },
  desktopActivity: () =>
    defaultDesktopActivity() as unknown as BindActivityInfo,
});

const actions = (
  set: StoreSet<IPictureTopUpStore>,
  get: StoreGet<IPictureTopUpStore>
): IPictureTopUpAction => ({
  ...commonActions(set, get),
  addMemberRecharge: async (params: any) => {
    set((state) => {
      state.paying = true;
    });
    const { code, data } = await PictureTopUpAPI.addMemberRecharge(params);
    if (code !== 200) {
      set((state) => {
        state.paying = false;
      });
      handlePaycode(code);
      return;
    }
    payWithAli(data.params, () => "todo modal");
    set((state) => {
      state.paying = false;
    });
  },
  addPayRecord: async (params: any) => {
    set((state) => {
      state.paying = true;
    });
    const { code, data } = await PictureTopUpAPI.addPayRecord(params);
    if (code !== 200) {
      set((state) => {
        state.paying = false;
      });
      handlePaycode(code);
      return;
    }
    payWithAli(data.params, () => "todo modal");
    set((state) => {
      state.paying = false;
    });
  },
  verifyRechargeCode: async (params: IVerifyRechargeCodeParams) => {
    const res = await PictureTopUpAPI.verifyRechargeCode(params);
    return res;
  },
});

export const usePictureTopUpStore = create<IPictureTopUpStore>()(
  composeMiddleware(
    [devtools, immer],
    (set: StoreSet<IPictureTopUpStore>, get: StoreGet<IPictureTopUpStore>) => ({
      ...initialState(),
      ...actions(set, get),
      ...getter(get),
    })
  )
);
